import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import store from 'store2';

import { HeaderProps } from 'types/interfaces';

import Banner from 'assets/profile-banner.png';
import CompleteProfileItems from './components/CompleteProfileItems';
import CompleteProfileProgress from './components/CompleteProfileProgress';
import ConfirmMember from './components/ConfirmMember';
import ConfirmPopUp from 'components/_shared/PopUp2/components/ConfirmPopUp';
import LoadingCircle from 'components/_shared/LoadingCircle';
import PhotoPopUp from 'components/_shared/PhotoPopUp';
import PopUp2 from 'components/_shared/PopUp2';
import ReferMemberPopUp from 'components/holder_profile/profile_2.0/ReferMember/components/ReferPopUp';
import RequestErrorModal from 'components/_shared/RequestErrorModal';
import Toast from 'components/_shared/Toast';
import Tooltip from './components/Tooltip';
import UnenrollHeader, { withExtensionRequestStatus } from './components/Unenroll';
import { ReferMember, SaveMember } from './components/HeaderButtons';
// import { CatchUp, Ellipsis } from './components/HeaderButtons';

import { formatExpirationToDate, isObjectEmpty } from 'util/index';

import useUpdatePublicProfile from 'hooks/useUpdatePublicProfile';

import ButtonBase from '@mui/material/ButtonBase';
import { createStyles, makeStyles } from '@mui/styles';
import useReliveOptions from 'hooks/api/useReliveOptions';
import useActiveMembershipExtensionOptions from 'hooks/api/useActiveMembershipExtensionOptions';
import ReactivateMember from './components/ReactivateMember';
import { Deployments, MemberSearchResult, ReliveOptionsDeployment } from 'types/index';
import ReactivateMembershipOption from 'components/membership/ReactivateMembershipOption';
import AlertModal from 'components/membership/AlertModal';
import DeploymentSelector from 'components/membership/DeploymentSelector';
import { convertToMemberSearchResult, daysUntilMembershipEnd, deploymentFromReliveOption, ExtensionRequestKind, handleStartRegistration, inactiveExtensionRequestKind, inactiveMembershipAction, isMaxDurationReasoning, membershipExpiringLabel, profileReliveButtonLabel, RELIVE_OPTIONS_UNAVAILABLE_REASON, reliveUnavailableReason } from 'util/membershipHelper';
import { shouldHideIntakeDeployment } from 'util/user';
import useIntakeAutofill from 'hooks/useIntakeAutofill';
import ProfileEligibilityCheck from 'components/membership/ProfileEligibilityCheck';
import ReinstateForm from 'components/membership/ReinstateForm';
import { reinstateDirectly } from 'api/homelesses';
import { decodeApiErrorFromResponse, parseApiErrorMessage } from 'util/parseApiErrorMessage';
import { getHomelessInfo } from 'redux/actions/homeless';

type ModalState =
    | { type: "NONE" }
    | { type: "REACTIVATE_MEMBER_OPTIONS" }
    | { type: "DEPLOYMENT_SELECTOR" }
    | { type: "UNABLE_TO_REACTIVATE_ALERT"; reason: string }
    | { type: "ELIGIBILITY_CHECK"; deployment: Deployments }
    | { type: "REINSTATE_MEMBERSHIP_FORM"; deploymentName: string; requestKind: ExtensionRequestKind };

const useStyles = makeStyles(() =>
    createStyles({
        active: {
            background: '#7378E8 !important',
            color: '#fff !important'
        },
        banner: {
            position: 'relative',
            display: 'flex',
            '& img': {
                minHeight: '150px',
                objectFit: 'cover',
                opacity: 0.8,
                width: '100%',
                '@media (max-width: 768px)': {
                    minHeight: '100px'
                }
            }
        },
        bannerContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '10px',
            padding: '20px 5% 0 5%'
        },
        buttonContainer: {
            position: 'absolute',
            right: '30px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
            margin: '10px 0',
            width: '100%',
            '@media (max-width: 928px)': {
                top: 0
            }
        },
        exited: {
            '& img': {
                boxShadow: '0 0 0 5px #FEF3E7'
            }
        },
        statusBanner: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: '16px',
            boxSizing: 'border-box',
            background: '#FEF3E7',
            color: '#7A4406',
            padding: '10px 20px',
            fontWeight: 700,
            fontSize: '20px',
            textTransform: 'uppercase',
            '@media (max-width: 768px)': {
                fontSize: '12px',
                gap: '8px',
                padding: '6px 12px'
            }
        },
        headerWrapper: {
            position: 'relative',
            padding: '120px 0px 32px 0px',
            '@media (max-width: 768px)': {
                padding: '100px 0 32px 0'
            }
        },
        loading: {
            pointerEvents: 'none', // Disable any pointer events when loading
            '&:hover $photoOverlay': {
                opacity: 0 // Ensure overlay doesn't show up on hover
            }
        },
        loadingCircleContainer: {
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '50%',
            height: '100%',
            width: '100%'
        },
        memberID: {
            fontSize: '17px',
            lineHeight: '21px'
        },
        memberInfoContainer: {
            display: 'flex',
            flexDirection: 'column',
            color: '#1F2152'
        },
        memberName: {
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '34px',
            marginBottom: '10px'
        },
        photoContainer: {
            position: 'absolute',
            left: '5%',
            top: '-60px',
            cursor: 'pointer',
            '&:hover $photoOverlay': {
                opacity: 1,
                pointerEvents: 'auto',
                transition: 'opacity 0.5s ease, visibility 0s',
                visibility: 'visible'
            },
            '& img': {
                borderRadius: '50%',
                boxShadow: '0 0 0 5px #fff',
                height: '160px',
                objectFit: 'cover',
                width: '160px'
            },
            '@media (max-width: 768px)': {
                left: '10%',
                top: '-25%'
            }
        },
        photoDisabled: {
            cursor: 'default'
        },
        photoOverlay: {
            position: 'absolute',
            top: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '50%',
            color: '#fff',
            height: '160px',
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.5s ease, visibility 0s 0.5s',
            visibility: 'hidden',
            width: '160px',
            '& i': {
                fontSize: '24px'
            }
        }
    })
);

const Header: React.FC<HeaderProps> = ({
    caseloads,
    // catchUpItems,
    confirmMemberPopUp,
    confirmUser,
    createMemberCaseload,
    deploymentOrgs,
    exited,
    holder,
    pending,
    profileItems,
    removeMemberFromMyMembers,
    saveMemberToMyMembers,
    setCompleteProfilePopup,
    setConfirmMemberPopUp,
    // setQuickCatchUp,
    setReenroll,
    setReferMemberPopUp,
    // setShowCatchUp,
    user,
    unsuccessful
}) => {
    const classes = useStyles();
    const isInitialRender = useRef(true);
    const userID = user.id;
    const history = useHistory();
    const { autofillIntake } = useIntakeAutofill();
    const dispatch = useDispatch();

    const { isError, isLoading, reset, updatePublicProfile } =
        useUpdatePublicProfile();

    const {
        first_name,
        id,
        last_name,
        member_id,
        middle_name,
        mobile_photo,
        name,
        photo,
        unenrolled_on,
        membership_end_date
    } = holder;

    const activeReferral = caseloads?.find(
        (caseload) =>
            caseload.homeless_id === id && caseload.status === 'pending'
    );
    const caseload = caseloads?.find((caseload) => caseload.homeless_id === id);
    const getLocalStorageTooltip = store.get('renderSaveMemberTooltip');
    const hasViewedTooltip =
        getLocalStorageTooltip && getLocalStorageTooltip[userID] ? true : false;
    const isSaved = caseloads?.some(
        (caseload: { homeless_id: number; status: string }) =>
            caseload.homeless_id === id && caseload.status === 'active'
    );
    const profilePercentage =
        (Object.keys(profileItems).filter(
            (item) =>
                profileItems[item].status === 'complete' ||
                profileItems[item].status === 'pending'
        ).length /
            Object.keys(profileItems).length) *
        100;

    const [photoPopUp, setPhotoPopUp] = useState(false);
    const [newProfilePicture, setNewProfilePicture] = useState(null);
    const [memberMenu, setMemberMenu] = useState(false);
    const [saved, setSaved] = useState(isSaved);
    const [showNotification, setShowNotification] = useState(false);
    const [showTooltip, setShowTooltip] = useState(true);

    const photoContainerClass = classNames({
        [classes.exited]: exited || unsuccessful,
        [classes.photoContainer]: true,
        [classes.photoDisabled]: isLoading || exited || unsuccessful
    });

    const updateProfilePhoto = useCallback(() => {
        const changedState = {} as { photo: Blob };
        const _formData = new FormData();

        if (newProfilePicture) {
            changedState['photo'] = newProfilePicture;
        }

        if (!isObjectEmpty(changedState)) {
            for (const [key, value] of Object.entries(changedState)) {
                _formData.append(`samaritan_member[${key}]`, value);
            }

            updatePublicProfile(id, _formData);
        }
    }, [id, newProfilePicture, updatePublicProfile]);

    const renderMemberName = () => {
        const trimmedFirstName = (first_name || '').trim();
        const trimmedLastName = (last_name || '').trim();
        const trimmedMiddleName = (middle_name || '').trim();

        if (!trimmedFirstName && !trimmedMiddleName && !trimmedLastName) {
            return name;
        }

        return `${trimmedFirstName} ${trimmedMiddleName} ${trimmedLastName}`.trim();
    };

    const { reliveOptions, isFetchingReliveOptions, reliveOptionsError } = useReliveOptions(
        id,
        Boolean(exited || unsuccessful)
    );
    const isActiveMembership = !exited && !unsuccessful && !pending;
    const { extensionOptions } = useActiveMembershipExtensionOptions(
        id,
        isActiveMembership
    );
    //modal state
    const [modalState, setModalState] = useState<ModalState>({ type: "NONE" });
    const isReinstating = useRef(false);

    const knownDeployments = user.partner?.deployments || [];
    //membership deployment
    const membersDeployment: Deployments | null = knownDeployments.find(deployment => deployment.id === holder.deployment_id) || null;

    const currentReenrollment = reliveOptions?.deployments_for_reenrollment.find(
        (deployment) => deployment.deployment_id === holder.deployment_id
    ) || null;
    const otherReenrollment = reliveOptions?.deployments_for_reenrollment.filter(
        (deployment) => deployment.deployment_id !== holder.deployment_id
    ) || [];

    const deploymentFromOption = (option: ReliveOptionsDeployment) =>
        deploymentFromReliveOption(option, knownDeployments);

    const currentDeploymentName =
        membersDeployment?.name ||
        currentReenrollment?.deployment_name ||
        holder.organization?.deployment?.name ||
        '';

    // Other deployments only. Current membership stays on the same-deployment path.
    // Grandfathered deployments and short_reasoning containing "commitment" stay hidden, same as pre-intake.
    const otherDeploymentOptions = otherReenrollment
        .map(deploymentFromOption)
        .filter((deployment) => !shouldHideIntakeDeployment(deployment));

    // Banner copy only. Click behavior follows inactiveMembershipAction.
    const extensionButtonLabel = profileReliveButtonLabel({
        canReinstate: Boolean(reliveOptions?.can_reinstate),
        canReinstateDirectly: Boolean(reliveOptions?.can_reinstate_directly),
        currentShortReasoning: currentReenrollment?.short_reasoning,
    });
    const shouldShowExtendMembershipButton = useMemo(() => {
        if (!(exited || unsuccessful)) return false;
        if (isFetchingReliveOptions) return false;
        if (reliveOptionsError) return true;
        if (!reliveOptions) return false;

        const hasOpenReenrollment = reliveOptions.deployments_for_reenrollment.some((option) => {
            const offersAction =
                option.can_sign_up || isMaxDurationReasoning(option.short_reasoning);
            if (!offersAction) return false;
            if (option.deployment_id === holder.deployment_id) return true;
            return !shouldHideIntakeDeployment(deploymentFromReliveOption(option, knownDeployments));
        });

        return (
            reliveOptions.can_reinstate ||
            reliveOptions.can_reinstate_directly ||
            isMaxDurationReasoning(reliveOptions.cannot_reinstate_reasoning) ||
            hasOpenReenrollment
        );
    }, [exited, unsuccessful, isFetchingReliveOptions, reliveOptionsError, reliveOptions, holder.deployment_id, knownDeployments]);

    const latestReinstateRequest = holder.relations?.latest_reinstate_request;
    const extensionRequestPending = latestReinstateRequest?.status === 'pending';
    const extensionRequestDenied = latestReinstateRequest?.status === 'cancelled';

    const showActiveRequestExtension =
        isActiveMembership && Boolean(extensionOptions?.can_request_extension);
    const daysUntilEnd = daysUntilMembershipEnd(membership_end_date);
    const showExpiringBanner = showActiveRequestExtension && daysUntilEnd != null;
    const showProfileBannerButton =
        !extensionRequestPending &&
        !(showActiveRequestExtension && extensionRequestDenied) &&
        (showActiveRequestExtension || shouldShowExtendMembershipButton);

    const showUnableToReactivate = (reason: string | null | undefined) => {
        setModalState({
            type: "UNABLE_TO_REACTIVATE_ALERT",
            reason: reason || RELIVE_OPTIONS_UNAVAILABLE_REASON
        });
    };

    const reinstateCurrentMembership = async () => {
        if (isReinstating.current) return;
        isReinstating.current = true;
        setModalState({ type: "NONE" });

        try {
            const response = await reinstateDirectly(id);
            if (!response.ok) {
                showUnableToReactivate(await decodeApiErrorFromResponse(response));
                return;
            }
            dispatch(getHomelessInfo('', '', '', id));
        } catch (error: unknown) {
            showUnableToReactivate(parseApiErrorMessage(error));
        } finally {
            isReinstating.current = false;
        }
    };

    const continueWithCurrentDeployment = () => {
        if (reliveOptionsError || !reliveOptions) {
            showUnableToReactivate(RELIVE_OPTIONS_UNAVAILABLE_REASON);
            return;
        }

        const action = inactiveMembershipAction(reliveOptions, {
            sameDeployment: true,
            shortReasoning: currentReenrollment?.short_reasoning,
            canSignUp: currentReenrollment?.can_sign_up
        });

        if (action === 'reinstateDirect') {
            reinstateCurrentMembership();
            return;
        }

        if (action === 'extensionForm') {
            setModalState({
                type: "REINSTATE_MEMBERSHIP_FORM",
                deploymentName: currentDeploymentName,
                requestKind: inactiveExtensionRequestKind(reliveOptions, true)
            });
            return;
        }

        if (action === 'reenroll' && currentReenrollment) {
            handleStartRegistration({
                member: convertToMemberSearchResult(holder),
                confirmedDeployment: deploymentFromOption(currentReenrollment),
                history,
                autofillIntake,
                dispatch
            });
            return;
        }

        showUnableToReactivate(reliveUnavailableReason(reliveOptions));
    };

    //handle deployment selection (either go to eligibility check or start registration)
    const handleDeploymentSelection = (deployment: Deployments) => {
        if (isMaxDurationReasoning(deployment.short_reasoning)) {
            setModalState({
                type: "REINSTATE_MEMBERSHIP_FORM",
                deploymentName: deployment.name,
                requestKind: reliveOptions
                    ? inactiveExtensionRequestKind(reliveOptions, false)
                    : 'reenrollment'
            });
            return;
        }

        if (deployment.is_eligibility_check_required) {
            setModalState({ type: "ELIGIBILITY_CHECK", deployment });
        } else {
            handleStartRegistration({ 
                member: convertToMemberSearchResult(holder), 
                confirmedDeployment: deployment,
                history,
                autofillIntake,
                dispatch
            });
        }
    };

    const memberMenuProps = {
        activeReferral,
        caseload,
        caseloadID: caseload?.id || null,
        createMemberCaseload,
        deploymentOrgs,
        hasViewedTooltip,
        isSaved,
        memberID: id,
        removeMemberFromMyMembers,
        saved,
        saveMemberToMyMembers,
        setMemberMenu,
        setReferMemberPopUp,
        showTooltip,
        setSaved
    };


    useEffect(() => {
        if (!isInitialRender.current) {
            setShowNotification(true);

            const timeout = setTimeout(() => {
                setShowNotification(false);
            }, 3000);

            return () => clearTimeout(timeout);
        } else {
            isInitialRender.current = false;
        }
    }, [saved]);

    useEffect(() => {
        if (newProfilePicture) {
            updateProfilePhoto();
        }
    }, [newProfilePicture, updateProfilePhoto]);

    useEffect(() => {
        setSaved(isSaved);
    }, [isSaved]);

    return (
        <>
            <Toast
                icon="fa-check-circle"
                message={
                    <>
                        {name} is {saved ? 'saved to' : 'removed from'} your{' '}
                        <b>"My Members"</b> list
                    </>
                }
                open={showNotification}
                severity={saved ? 'success' : 'warning'}
            />
            <div className={classes.banner}>
                <img src={Banner} alt="banner" />
                {(showExpiringBanner || pending || exited || unsuccessful) && (
                    <div className={classes.statusBanner}>
                        {showExpiringBanner && daysUntilEnd != null && (
                            <span>
                                {withExtensionRequestStatus(
                                    membershipExpiringLabel(daysUntilEnd),
                                    latestReinstateRequest
                                )}
                            </span>
                        )}
                        {pending && <span>Membership status: pending</span>}
                        {(exited || unsuccessful) && (
                            <UnenrollHeader
                                exited={exited}
                                unsuccessful={unsuccessful}
                                unenrolled_on={unenrolled_on}
                                latestReinstateRequest={latestReinstateRequest}
                                setReenroll={setReenroll}
                            />
                        )}
                    </div>
                )}
            </div>
            {!caseload &&
                !hasViewedTooltip &&
                showTooltip &&
                !exited &&
                !unsuccessful && (
                    <Tooltip
                        name={name}
                        userID={userID}
                        setShowTooltip={setShowTooltip}
                    />
                )}
            {/* {!exited && !unsuccessful && (
                <div className={classes.buttonContainer}>
                    {catchUpItems.length > 0 && (
                        <CatchUp
                            setQuickCatchUp={setQuickCatchUp}
                            setShowCatchUp={setShowCatchUp}
                        />
                    )}
                    <Ellipsis setShowMemberMenu={setMemberMenu} />
                </div>
            )} */}
            {!exited && !unsuccessful && (
                <div className={classes.buttonContainer}>
                    {!exited && deploymentOrgs?.length && (
                        <ReferMember
                            isSaved={isSaved}
                            setReferMemberPopup={setReferMemberPopUp}
                        />
                    )}
                    {!exited && (
                        <SaveMember
                            activeReferral={activeReferral}
                            caseload={caseload}
                            caseloadID={caseload?.id || null}
                            createMemberCaseload={createMemberCaseload}
                            hasViewedTooltip={hasViewedTooltip}
                            isSaved={isSaved}
                            memberID={id}
                            removeMemberFromMyMembers={
                                removeMemberFromMyMembers
                            }
                            saved={saved}
                            saveMemberToMyMembers={saveMemberToMyMembers}
                            showTooltip={showTooltip}
                            setSaved={setSaved}
                        />
                    )}
                </div>
            )}
            <div className={classes.headerWrapper}>
                {!exited &&
                    !pending &&
                    !unsuccessful &&
                    profilePercentage !== 100 &&
                    !showProfileBannerButton && (
                        <CompleteProfileItems
                            profileItems={profileItems}
                            setCompleteProfilePopup={setCompleteProfilePopup}
                        />
                    )}
                {pending && confirmUser && (
                    <ConfirmMember
                        setConfirmMemberPopUp={setConfirmMemberPopUp}
                    />
                )}
                {showProfileBannerButton && (
                    <ReactivateMember
                    label={showActiveRequestExtension ? 'Request Extension' : extensionButtonLabel}
                    setShowReactivateMemberOptionsPopup={() => {
                        if (showActiveRequestExtension) {
                            setModalState({
                                type: "REINSTATE_MEMBERSHIP_FORM",
                                deploymentName: currentDeploymentName,
                                requestKind: 'activeMembership'
                            });
                            return;
                        }

                        if (reliveOptionsError || !reliveOptions) {
                            showUnableToReactivate(RELIVE_OPTIONS_UNAVAILABLE_REASON);
                            return;
                        }

                        if (otherDeploymentOptions.length === 0) {
                            continueWithCurrentDeployment();
                            return;
                        }

                        setModalState({ type: "REACTIVATE_MEMBER_OPTIONS" });
                    }}
                    />
                )}
                <div
                    className={`${photoContainerClass} ${
                        isLoading ? classes.loading : ``
                    }`}
                >
                    {isLoading && (
                        <div className={classes.loadingCircleContainer}>
                            <LoadingCircle />
                        </div>
                    )}
                    <img
                        src={mobile_photo}
                        alt="profile"
                        style={
                            pending ? { boxShadow: '0 0 0 5px #F8E8E8' } : {}
                        }
                    />
                    {!exited && !unsuccessful && (
                        <ButtonBase
                            className={classes.photoOverlay}
                            disabled={isLoading || exited || unsuccessful}
                            component="div"
                            onClick={() =>
                                !isLoading &&
                                !exited &&
                                !unsuccessful &&
                                setPhotoPopUp(true)
                            }
                        >
                            <i className="fas fa-pencil" />
                        </ButtonBase>
                    )}
                    {!exited &&
                        !pending &&
                        !unsuccessful &&
                        !showProfileBannerButton &&
                        profilePercentage !== 100 && (
                        <CompleteProfileProgress
                            percentage={profilePercentage}
                        />
                    )}
                </div>
                <div className={classes.bannerContainer}>
                    <div className={classes.memberInfoContainer}>
                        <span className={classes.memberName}>
                            {renderMemberName()}
                        </span>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}
                        >
                            {!exited && !unsuccessful && (
                                <span className={classes.memberID}>
                                    Graduation:{' '}
                                    {formatExpirationToDate(
                                        membership_end_date
                                    )}
                                </span>
                            )}
                            <span className={classes.memberID}>
                                Insurance ID: {member_id ? member_id : '-'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {isError && (
                <RequestErrorModal
                    open={isError}
                    onClose={() => reset()}
                    message="Changes couldn't be saved."
                    retryFunc={updateProfilePhoto}
                />
            )}
            {confirmMemberPopUp && (
                <PopUp2
                    modalClose={true}
                    onClose={() => setConfirmMemberPopUp(false)}
                    open={confirmMemberPopUp}
                    photo={photo}
                    title="confirm"
                >
                    <ConfirmPopUp
                        holder={holder}
                        onClose={() => setConfirmMemberPopUp(false)}
                    />
                </PopUp2>
            )}
            {memberMenu && (
                <PopUp2
                    customClasses={{ content: { gap: '0', padding: '0' } }}
                    onClose={() => setMemberMenu(false)}
                    open={memberMenu}
                    title="refer"
                >
                    <ReferMemberPopUp {...memberMenuProps} />
                </PopUp2>
            )}
            {photoPopUp && (
                <PhotoPopUp
                    fromProfilePhoto={true}
                    profilePhotoURL={photo}
                    handleModalClick={() => setPhotoPopUp(false)}
                    setProfilePicture={setNewProfilePicture}
                />
            )}
            {modalState.type === "REACTIVATE_MEMBER_OPTIONS" && reliveOptions && (
                <ReactivateMembershipOption
                    member={holder}
                    deploymentName={currentDeploymentName}
                    reliveOptions={reliveOptions}
                    currentDeployment={
                        currentReenrollment
                            ? {
                                canSignUp: currentReenrollment.can_sign_up,
                                shortReasoning: currentReenrollment.short_reasoning
                            }
                            : null
                    }
                    otherDeployments={otherDeploymentOptions.map((deployment) => ({
                        canSignUp: deployment.can_sign_up,
                        shortReasoning: deployment.short_reasoning
                    }))}
                    onSelectOption={(sameDeployment: boolean) => {
                        setModalState({ type: "NONE" });
                        if (sameDeployment) {
                            continueWithCurrentDeployment();
                        } else {
                            setModalState({ type: "DEPLOYMENT_SELECTOR" });
                        }
                    }}
                    onClose={() => setModalState({ type: "NONE" })}
                />
            )}
            {modalState.type === "DEPLOYMENT_SELECTOR" && (
                <DeploymentSelector
                    onClose={() => setModalState({ type: "NONE" })}
                    deployments={otherDeploymentOptions}
                    onSelectDeployment={(deployment: Deployments) => {
                        setModalState({ type: "NONE" });
                        handleDeploymentSelection(deployment);
                    }}
                />
            )}
            {modalState.type === "UNABLE_TO_REACTIVATE_ALERT" && (
                <AlertModal
                    title="Unable to Extend Membership"
                    subtext={modalState.reason}
                    icon="fa-exclamation-circle"
                    colorType="yellow"
                    handleCloseClick={() => setModalState({ type: "NONE" })}
                />
            )}
            {modalState.type === "ELIGIBILITY_CHECK" && (
                <ProfileEligibilityCheck
                    member={holder}
                    closeButtonText="Close"
                    handleCloseClick={() => setModalState({ type: "NONE" })}
                    selectedDeployment={modalState.deployment}
                    handleEligible={(member: MemberSearchResult) => {
                        setModalState({ type: "NONE" });
                        handleStartRegistration({ 
                            member: member, 
                            confirmedDeployment: modalState.deployment,
                            history,
                            autofillIntake,
                            dispatch
                        });
                    }}
                />
            )}
            {modalState.type === "REINSTATE_MEMBERSHIP_FORM" && (
                <ReinstateForm
                    member={holder}
                    deploymentName={modalState.deploymentName}
                    requestKind={modalState.requestKind}
                    closeButtonText="Back to Profile"
                    onClose={() => setModalState({ type: "NONE" })}
                    onSubmitted={() => {
                        dispatch(getHomelessInfo('', '', '', id));
                    }}
                    onFinish={() => {
                        setModalState({ type: "NONE" });
                        dispatch(getHomelessInfo('', '', '', id));
                    }}
                />
            )}
        </>
    );
};

export default Header;
